import { useState, useEffect } from "react";
import styles from "./DeleteSubscriptionPlanForm.module.css";
import Button from "../../UI/Button/Button";
import FeedbackModal from "../../UI/FeedbackModal/FeedbackModal";
import {
  getSubscriptionPlans,
  deleteSubscriptionPlan,
} from "../../../services/subscription-plan-service";

export default function DeleteSubscriptionPlanForm() {
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState("info");
  const [modalTitle, setModalTitle] = useState("");
  const [modalMessage, setModalMessage] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    setIsLoading(true);
    try {
      const result = await getSubscriptionPlans();
      if (result.success) {
        setPlans(result.data || []);
      } else {
        setModalType("error");
        setModalTitle("Error");
        setModalMessage(result.error || "Failed to load subscription plans");
        setModalOpen(true);
      }
    } catch (error) {
      setModalType("error");
      setModalTitle("Error");
      setModalMessage("Failed to load subscription plans");
      setModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePlanSelect = (e) => {
    const planId = e.target.value;
    const plan = plans.find((p) => p._id === planId);
    setSelectedPlan(plan || null);
    setConfirmDelete(false);
  };

  const handleDelete = async () => {
    if (!selectedPlan) {
      setModalType("error");
      setModalTitle("Error");
      setModalMessage("Please select a subscription plan to delete");
      setModalOpen(true);
      return;
    }

    if (selectedPlan.status === "active") {
      setModalType("error");
      setModalTitle("Cannot Delete Active Plan");
      setModalMessage(
        "Cannot delete an active subscription plan. Active plans must remain active while clients are paying. Please set the status to inactive first, or cancel the subscription through the payment system.",
      );
      setModalOpen(true);
      return;
    }

    if (!confirmDelete) {
      setModalType("error");
      setModalTitle("Confirmation Required");
      setModalMessage(
        "Please confirm deletion by checking the confirmation box",
      );
      setModalOpen(true);
      return;
    }

    setIsDeleting(true);

    try {
      const result = await deleteSubscriptionPlan(selectedPlan._id);

      if (result.success) {
        setModalType("success");
        setModalTitle("Success");
        setModalMessage(
          `Subscription plan "${selectedPlan.product || selectedPlan.projectId || selectedPlan._id}" deleted successfully!`,
        );
        setModalOpen(true);
        await loadPlans();
        setSelectedPlan(null);
        setConfirmDelete(false);
      } else {
        throw new Error(result.error || "Failed to delete subscription plan");
      }
    } catch (error) {
      setModalType("error");
      setModalTitle("Error");
      setModalMessage(error.message || "Failed to delete subscription plan");
      setModalOpen(true);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <div className={styles.projectForm}>
        <div className={styles.formGroup}>
          <label htmlFor="planSelect" className={styles.label}>
            Select Subscription Plan to Delete *
          </label>
          <select
            id="planSelect"
            name="planSelect"
            value={selectedPlan?._id || ""}
            onChange={handlePlanSelect}
            className={styles.select}
            required
            disabled={isLoading || isDeleting}
          >
            <option value="">
              {isLoading ? "Loading plans..." : "Select a subscription plan..."}
            </option>
            {plans.map((plan) => (
              <option key={plan._id} value={plan._id}>
                {plan.product || plan.projectId || plan._id} - {plan.status} (
                {plan.clientEmail})
                {plan.status === "active" ? " ⚠️ Cannot delete" : ""}
              </option>
            ))}
          </select>
        </div>

        {selectedPlan && (
          <div className={styles.formGroup}>
            <div
              style={{
                padding: "16px",
                background: "#fee2e2",
                border: "1px solid #fecaca",
                borderRadius: "8px",
                marginBottom: "16px",
              }}
            >
              <h3
                style={{
                  margin: "0 0 8px 0",
                  color: "#991b1b",
                  fontSize: "16px",
                  fontWeight: "600",
                }}
              >
                Subscription Plan Details:
              </h3>
              <p
                style={{ margin: "4px 0", color: "#374151", fontSize: "14px" }}
              >
                <strong>Product:</strong> {selectedPlan.product || "N/A"}
              </p>
              <p
                style={{ margin: "4px 0", color: "#374151", fontSize: "14px" }}
              >
                <strong>Project ID:</strong> {selectedPlan.projectId || "N/A"}
              </p>
              <p
                style={{ margin: "4px 0", color: "#374151", fontSize: "14px" }}
              >
                <strong>Client Email:</strong>{" "}
                {selectedPlan.clientEmail || "N/A"}
              </p>
              <p
                style={{ margin: "4px 0", color: "#374151", fontSize: "14px" }}
              >
                <strong>Tier:</strong>{" "}
                {selectedPlan.projectTier ||
                  selectedPlan.subscriptionPlan ||
                  "N/A"}
              </p>
              <p
                style={{ margin: "4px 0", color: "#374151", fontSize: "14px" }}
              >
                <strong>Status:</strong> {selectedPlan.status || "N/A"}
              </p>
              <p
                style={{ margin: "4px 0", color: "#374151", fontSize: "14px" }}
              >
                <strong>Amount:</strong> {selectedPlan.amount || "0"}{" "}
                {selectedPlan.currency || "RON"}
              </p>
              {selectedPlan.status === "active" && (
                <div
                  style={{
                    marginTop: "12px",
                    padding: "12px",
                    background: "#fee2e2",
                    border: "1px solid #fecaca",
                    borderRadius: "6px",
                  }}
                >
                  <p
                    style={{
                      margin: "0",
                      color: "#991b1b",
                      fontSize: "13px",
                      fontWeight: "600",
                    }}
                  >
                    ⚠️ Warning: This plan is ACTIVE. Active plans cannot be
                    deleted to prevent interruption of client payments. Set
                    status to inactive first.
                  </p>
                </div>
              )}
            </div>

            <div className={styles.formGroup}>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={confirmDelete}
                  onChange={(e) => setConfirmDelete(e.target.checked)}
                  disabled={isDeleting || selectedPlan?.status === "active"}
                  style={{
                    width: "18px",
                    height: "18px",
                    cursor:
                      selectedPlan?.status === "active"
                        ? "not-allowed"
                        : "pointer",
                    opacity: selectedPlan?.status === "active" ? 0.5 : 1,
                  }}
                />
                <span
                  style={{
                    color:
                      selectedPlan?.status === "active" ? "#9ca3af" : "#991b1b",
                    fontWeight: "600",
                  }}
                >
                  {selectedPlan?.status === "active"
                    ? "Cannot delete active plans. Set status to inactive first."
                    : "I confirm that I want to delete this subscription plan. This action cannot be undone."}
                </span>
              </label>
            </div>
          </div>
        )}

        <div className={styles.formActions}>
          <Button
            type="button"
            variant="default"
            onClick={handleDelete}
            disabled={
              !selectedPlan ||
              !confirmDelete ||
              isDeleting ||
              isLoading ||
              selectedPlan?.status === "active"
            }
            style={{
              background:
                confirmDelete &&
                selectedPlan &&
                selectedPlan.status !== "active"
                  ? "#dc2626"
                  : undefined,
              borderColor:
                confirmDelete &&
                selectedPlan &&
                selectedPlan.status !== "active"
                  ? "#dc2626"
                  : undefined,
              opacity: selectedPlan?.status === "active" ? 0.5 : 1,
            }}
          >
            {isDeleting
              ? "Deleting..."
              : selectedPlan?.status === "active"
                ? "Cannot Delete Active Plan"
                : "Delete Subscription Plan"}
          </Button>
        </div>
      </div>

      <FeedbackModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalTitle}
        message={modalMessage}
        type={modalType}
        confirmText="Close"
      />
    </>
  );
}
